import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import FlightTakeoffRoundedIcon from '@mui/icons-material/FlightTakeoffRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { buildPublicFileUrl } from '../../config/env';
import {
  DataTableShell,
  DetailField,
  EmptyState,
  PageHeader,
  PageLayout,
  StatCard,
  StatusBadge,
} from '../../components/ui';
import {
  cancelExitEntryRequest,
  fetchExitEntryPrRequests,
  updateRequestStatus,
} from '../../services/exitEntryApi';

const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(date);
};

const getRequesterName = (request) =>
  request?.user?.name
    || request?.employee?.name
    || request?.employee_name
    || request?.requester_name
    || 'N/A';

const canActOnRequest = (role, request) =>
  role === 'hr_admin' && normalizeStatus(request?.status) === 'pending';

const canCancelRequest = (role, request) =>
  role !== 'employee' && normalizeStatus(request?.status) === 'pending';

export default function PrExitEntryPage() {
  const theme = useTheme();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role] = useState(localStorage.getItem('role') || 'employee');
  const [processingId, setProcessingId] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await fetchExitEntryPrRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const metrics = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter((item) => normalizeStatus(item.status) === 'pending').length;
    const approved = requests.filter((item) => normalizeStatus(item.status) === 'approved').length;
    const rejected = requests.filter((item) => normalizeStatus(item.status) === 'rejected').length;
    return { total, pending, approved, rejected };
  }, [requests]);

  const handleCancel = async (requestId) => {
    setProcessingId(requestId);
    try {
      await cancelExitEntryRequest(requestId);
      toast.success('Request cancelled successfully!');
      await loadRequests();
    } catch (error) {
      console.error('Error cancelling request:', error);
      toast.error('Failed to cancel request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    setProcessingId(requestId);
    try {
      await updateRequestStatus(requestId, { status: newStatus });
      toast.success(`Request ${newStatus} successfully!`);
      await loadRequests();
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error('Failed to update request status');
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setOpenView(true);
  };

  const handleCloseView = () => {
    setOpenView(false);
    setSelectedRequest(null);
  };

  const renderActions = (request) => {
    const busy = processingId === request.id;

    return (
      <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityOutlinedIcon />}
          onClick={() => handleViewDetails(request)}
        >
          View
        </Button>

        {canActOnRequest(role, request) ? (
          <>
            <Button
              variant="outlined"
              color="success"
              size="small"
              disabled={busy}
              startIcon={<CheckCircleOutlineRoundedIcon />}
              onClick={() => handleStatusUpdate(request.id, 'approved')}
            >
              Approve
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={busy}
              startIcon={<CancelOutlinedIcon />}
              onClick={() => handleStatusUpdate(request.id, 'rejected')}
            >
              Reject
            </Button>
          </>
        ) : null}

        {canCancelRequest(role, request) ? (
          <Button
            variant="text"
            color="warning"
            size="small"
            disabled={busy}
            startIcon={<DoDisturbOnOutlinedIcon />}
            onClick={() => handleCancel(request.id)}
          >
            Cancel
          </Button>
        ) : null}
      </Stack>
    );
  };

  const mobileCards = (
    <Stack spacing={1.4}>
      {requests.map((request) => (
        <Box
          key={request.id}
          sx={{
            p: 1.4,
            borderRadius: theme.hr?.radius?.lg || 2,
            border: '1px solid',
            borderColor: theme.hr?.line?.soft || 'divider',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.04)
                : alpha(theme.palette.background.paper, 0.82),
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 850 }} noWrap>
                {getRequesterName(request)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatValue(request.visa_type)} · {formatDate(request.validity_from_date)}
              </Typography>
            </Box>
            <StatusBadge status={request.status} />
          </Stack>

          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <DetailField compact label="From" value={formatDate(request.validity_from_date)} />
            </Grid>
            <Grid item xs={6}>
              <DetailField compact label="To" value={formatDate(request.validity_to_date)} />
            </Grid>
          </Grid>

          <Box sx={{ mt: 1.2 }}>{renderActions(request)}</Box>
        </Box>
      ))}
    </Stack>
  );

  return (
    <PageLayout backgroundAccent>
      <PageHeader
        variant="dashboard"
        overline="Exit / entry"
        title="PR exit / entry requests"
        subtitle="Review exit-entry request records, attachments, validity windows, and approval state from one focused workspace."
        actions={
          <Button
            variant="contained"
            startIcon={<RefreshRoundedIcon />}
            onClick={loadRequests}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      <Grid container spacing={1.8} sx={{ mb: 2.1 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            tone="dashboard"
            title="Total Requests"
            value={metrics.total}
            hint="PR queue records"
            icon={<FlightTakeoffRoundedIcon />}
            color="primary.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            tone="dashboard"
            title="Pending"
            value={metrics.pending}
            hint="Waiting for decision"
            icon={<DescriptionOutlinedIcon />}
            color="warning.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            tone="dashboard"
            title="Approved"
            value={metrics.approved}
            hint="Completed approvals"
            icon={<CheckCircleOutlineRoundedIcon />}
            color="success.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            tone="dashboard"
            title="Rejected"
            value={metrics.rejected}
            hint="Declined requests"
            icon={<CancelOutlinedIcon />}
            color="error.main"
            loading={loading}
          />
        </Grid>
      </Grid>

      <DataTableShell
        tone="dashboard"
        title="Exit / entry PR list"
        subtitle="Validity dates, visa details, current status, and available actions."
        loading={loading}
        empty={
          requests.length === 0 ? (
            <EmptyState
              compact
              title="No exit / entry PR requests"
              description="There are no PR exit-entry requests in this queue."
              icon={FlightTakeoffRoundedIcon}
            />
          ) : null
        }
        mobileCards={mobileCards}
      >
        <Table size="small" aria-label="Exit entry PR requests table">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Visa Type</TableCell>
              <TableCell>Validity From</TableCell>
              <TableCell>Validity To</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: theme.hr?.radius?.sm || 1.5,
                        display: 'grid',
                        placeItems: 'center',
                        color: 'primary.main',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <PersonOutlineRoundedIcon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                        {getRequesterName(request)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Request #{request.id}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{formatValue(request.visa_type)}</TableCell>
                <TableCell>{formatDate(request.validity_from_date)}</TableCell>
                <TableCell>{formatDate(request.validity_to_date)}</TableCell>
                <TableCell>
                  <StatusBadge status={request.status} />
                </TableCell>
                <TableCell align="right">{renderActions(request)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataTableShell>

      <Dialog open={openView} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5">Exit / entry request details</Typography>
              <Typography variant="body2" color="text.secondary">
                Full request context and supporting document state.
              </Typography>
            </Box>
            <IconButton onClick={handleCloseView} aria-label="Close request details">
              <CancelOutlinedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest ? (
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <StatusBadge status={selectedRequest.status} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Request #{selectedRequest.id}
                </Typography>
              </Stack>

              <Grid container spacing={1.2}>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="User Name"
                    value={getRequesterName(selectedRequest)}
                    icon={<PersonOutlineRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="Visa Type"
                    value={selectedRequest.visa_type}
                    icon={<FlightTakeoffRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="Validity From"
                    value={formatDate(selectedRequest.validity_from_date)}
                    icon={<CalendarMonthRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="Validity To"
                    value={formatDate(selectedRequest.validity_to_date)}
                    icon={<CalendarMonthRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailField
                    label="Country"
                    value={selectedRequest.country}
                    icon={<PublicRoundedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailField
                    label="Phone Number"
                    value={selectedRequest.phone_number}
                    icon={<PhoneOutlinedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailField
                    label="Period"
                    value={selectedRequest.period_in_days ? `${selectedRequest.period_in_days} days` : '-'}
                    icon={<DescriptionOutlinedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DetailField
                    label="Location In Country"
                    value={selectedRequest.location_in_country}
                    icon={<PlaceOutlinedIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DetailField
                    label="Reason"
                    value={selectedRequest.reason}
                    icon={<DescriptionOutlinedIcon fontSize="small" />}
                    sx={{ '& .MuiTypography-body2': { whiteSpace: 'pre-wrap' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DetailField
                    label="Attachment"
                    value={
                      selectedRequest.document_path ? (
                        <Link
                          href={buildPublicFileUrl(selectedRequest.document_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontWeight: 800 }}
                        >
                          View document
                        </Link>
                      ) : (
                        'Attachment missing'
                      )
                    }
                    icon={<AttachFileRoundedIcon fontSize="small" />}
                  />
                </Grid>
              </Grid>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseView}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}
